import { useEffect } from "react";

import { useApolloClient } from "@apollo/client";
import { chunkArray } from "@intelligems/ig-utils/common";
import {
  FetchProductAndVariantImagesDocument,
  type FetchProductAndVariantImagesQuery,
} from "@src/generated";
import { useImmer } from "use-immer";

/** `nodes(ids:)` refuses more than 250 ids in one call. */
const SHOPIFY_ID_LIMIT = 250;

/**
 * What the canvas says about a product or variant it only holds the GID of:
 * the product's title, the variant's own title when the GID names a variant
 * (null for a product), and the variant's price as Shopify's decimal string
 * (null for a product, which has no single price).
 */
export interface ProductOrVariantSummary {
  title: string;
  variantTitle: string | null;
  price: string | null;
}

/**
 * Thumbnail URL and summary for each Shopify GID given — product or variant,
 * mixed freely in one list. A variant with no image of its own falls back to
 * its product's featured image, so a caller can always pass the most
 * specific id it holds and still get the picture the shopper would see.
 *
 * Keyed by the GID that was asked for, so a caller never has to know whether
 * the URL it got came from the variant or the product behind it.
 */
export const useProductAndVariantSummaries = (
  gids: string[]
): {
  imageUrlByGid: Record<string, string | null>;
  summaryByGid: Record<string, ProductOrVariantSummary>;
  loading: boolean;
} => {
  const client = useApolloClient();
  const [resolved, setResolved] = useImmer<{
    imageUrlByGid: Record<string, string | null>;
    summaryByGid: Record<string, ProductOrVariantSummary>;
  }>({ imageUrlByGid: {}, summaryByGid: {} });
  const [loading, setLoading] = useImmer(false);
  // Effects key off identity, and the caller rebuilds this array each render.
  const requestKey = gids.join(",");

  useEffect(() => {
    let cancelled = false;
    const ids = requestKey === "" ? [] : requestKey.split(",");
    if (ids.length === 0) {
      setResolved(() => ({ imageUrlByGid: {}, summaryByGid: {} }));
      return;
    }

    const fetchSummaries = async () => {
      setLoading(() => true);
      try {
        const results = await Promise.all(
          chunkArray(ids, SHOPIFY_ID_LIMIT).map(async (chunk) =>
            client.query<FetchProductAndVariantImagesQuery>({
              query: FetchProductAndVariantImagesDocument,
              variables: { ids: chunk },
              fetchPolicy: "cache-first",
            })
          )
        );
        if (cancelled) {
          return;
        }
        setResolved(() =>
          resolveSummaries(
            results.flatMap((result) => result.data?.nodes ?? [])
          )
        );
      } catch (error) {
        if (cancelled) {
          return;
        }
        // A missing thumbnail or title is a cosmetic loss; the panel renders
        // its placeholder rather than failing the whole preview.
        console.error("Error fetching product and variant summaries:", error);
        setResolved(() => ({ imageUrlByGid: {}, summaryByGid: {} }));
      } finally {
        if (!cancelled) {
          setLoading(() => false);
        }
      }
    };

    void fetchSummaries();
    return () => {
      cancelled = true;
    };
  }, [requestKey, client]);

  return { ...resolved, loading };
};

const resolveSummaries = (
  nodes: FetchProductAndVariantImagesQuery["nodes"]
): {
  imageUrlByGid: Record<string, string | null>;
  summaryByGid: Record<string, ProductOrVariantSummary>;
} => {
  const imageUrlByGid: Record<string, string | null> = {};
  const summaryByGid: Record<string, ProductOrVariantSummary> = {};
  for (const node of nodes) {
    if (!node) {
      continue;
    }
    if (node.__typename === "Product") {
      imageUrlByGid[node.id] = node.featuredImage?.url ?? null;
      summaryByGid[node.id] = {
        title: node.title,
        variantTitle: null,
        price: null,
      };
    } else if (node.__typename === "ProductVariant") {
      imageUrlByGid[node.id] =
        node.image?.url ?? node.product.featuredImage?.url ?? null;
      summaryByGid[node.id] = {
        title: node.product.title,
        variantTitle: node.title,
        price: typeof node.price === "string" ? node.price : null,
      };
    }
  }
  return { imageUrlByGid, summaryByGid };
};
